import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableRowLink from "@dashboard/components/TableRowLink";
import { useWsmProductComposeTabQuery } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { renderCollection } from "@dashboard/misc";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { WsmAmount } from "@dashboard/wsmShared/money";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { getFeeScopeLabel, getPromptTypeLabel } from "../../labels";
import {
  complianceMessages,
  feeMessages,
  optionSetMessages,
  productTabMessages,
} from "../../messages";
import {
  wsmComplianceUrl,
  wsmFeeAddUrl,
  wsmFeeUrl,
  wsmOptionSetAddUrl,
  wsmOptionSetUrl,
} from "../../urls";
import { formatStateCodes } from "../../utils";
import WsmOneLine from "../WsmOneLine/WsmOneLine";
import { WsmRailRow } from "../WsmRail/WsmRail";
import WsmYesNo from "../WsmYesNo/WsmYesNo";

/**
 * What this product asks, what it charges and what it warns, on the product
 * itself. The Catalog lists answer "show me every question in the store"; this
 * answers "what is on THIS one", which is the question a merchant editing a
 * product actually has. One round trip serves all three: the fork extends
 * stock's Product type with the three fields (product_extension.py).
 */

const OPTION_SET_COLUMNS = 4;
const FEE_COLUMNS = 4;

export interface WsmProductComposeCardProps {
  productId: string;
  productName?: string | null;
}

export const WsmProductComposeCard = ({ productId, productName }: WsmProductComposeCardProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const { data, loading, error } = useWsmProductComposeTabQuery({
    variables: { id: productId },
    fetchPolicy: "cache-and-network",
  });
  // The create screens take the product with them, so a merchant never picks a
  // product from a screen they reached FROM that product.
  const prefill = { id: productId, name: productName };
  // undefined, not [], is what makes renderCollection paint skeleton rows.
  // There is no Product.wsmOptionSets: a question is shared across products, so
  // the tab reads the root fields filtered by this product (wsm/PRODUCTS.md).
  const optionSets = loading ? undefined : (mapEdgesToItems(data?.wsmOptionSets) ?? []);
  const fees = loading ? undefined : (mapEdgesToItems(data?.wsmFees) ?? []);
  const compliance = data?.wsmProductCompliance;

  if (error) {
    return (
      <DashboardCard data-test-id="wsm-product-compose-error">
        <DashboardCard.Content>
          <Text size={3} color="critical1">
            {intl.formatMessage(productTabMessages.loadFailed)}
          </Text>
        </DashboardCard.Content>
      </DashboardCard>
    );
  }

  return (
    <Box data-test-id="wsm-product-compose">
      <DashboardCard data-test-id="wsm-product-option-sets">
        <DashboardCard.Header>
          <DashboardCard.Title>
            {intl.formatMessage(productTabMessages.optionSetsTitle, {
              count: optionSets?.length ?? 0,
            })}
          </DashboardCard.Title>
          <DashboardCard.Toolbar>
            <Button
              variant="secondary"
              onClick={() => navigate(wsmOptionSetAddUrl(prefill))}
              data-test-id="wsm-product-add-option-set"
            >
              {intl.formatMessage(optionSetMessages.create)}
            </Button>
          </DashboardCard.Toolbar>
        </DashboardCard.Header>
        <DashboardCard.Content paddingX={0}>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(optionSetMessages.colQuestion)}</TableCell>
                <TableCell>{intl.formatMessage(optionSetMessages.colPromptType)}</TableCell>
                <TableCell>{intl.formatMessage(productTabMessages.colChoices)}</TableCell>
                <TableCell>{intl.formatMessage(optionSetMessages.colRequired)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                optionSets,
                optionSet => (
                  <TableRowLink
                    key={optionSet ? optionSet.id : "skeleton"}
                    hover={!!optionSet}
                    href={optionSet ? wsmOptionSetUrl(optionSet.id) : undefined}
                    data-test-id="wsm-product-option-set-row"
                  >
                    <TableCell>
                      {optionSet ? (
                        <WsmOneLine>{optionSet.label || optionSet.name}</WsmOneLine>
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {optionSet ? (
                        intl.formatMessage(getPromptTypeLabel(optionSet.promptType))
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>{optionSet ? optionSet.values.length : <Skeleton />}</TableCell>
                    <TableCell>
                      {optionSet ? <WsmYesNo value={optionSet.required} /> : <Skeleton />}
                    </TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-product-option-sets-empty">
                    <TableCell colSpan={OPTION_SET_COLUMNS}>
                      <Box paddingY={6}>{intl.formatMessage(optionSetMessages.noOptionSets)}</Box>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>

      <CardSpacer />

      <DashboardCard data-test-id="wsm-product-fees">
        <DashboardCard.Header>
          <DashboardCard.Title>
            {intl.formatMessage(productTabMessages.feesTitle, { count: fees?.length ?? 0 })}
          </DashboardCard.Title>
          <DashboardCard.Toolbar>
            <Button
              variant="secondary"
              onClick={() => navigate(wsmFeeAddUrl(prefill))}
              data-test-id="wsm-product-add-fee"
            >
              {intl.formatMessage(feeMessages.create)}
            </Button>
          </DashboardCard.Toolbar>
        </DashboardCard.Header>
        <DashboardCard.Content paddingX={0}>
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(feeMessages.colLabel)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colAmount)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colApplyTo)}</TableCell>
                <TableCell>{intl.formatMessage(feeMessages.colRequired)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                fees,
                fee => (
                  <TableRowLink
                    key={fee ? fee.id : "skeleton"}
                    hover={!!fee}
                    href={fee ? wsmFeeUrl(fee.id) : undefined}
                    data-test-id="wsm-product-fee-row"
                  >
                    <TableCell>
                      {fee ? <WsmOneLine>{fee.label}</WsmOneLine> : <Skeleton />}
                    </TableCell>
                    <TableCell>
                      {fee ? (
                        <WsmAmount
                          amount={fee.amount}
                          currency={fee.currencyCode}
                          basis={fee.basis === "PERCENT" ? "percent" : "fixed"}
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell>
                      {fee ? intl.formatMessage(getFeeScopeLabel(fee.applyTo)) : <Skeleton />}
                    </TableCell>
                    <TableCell>
                      {fee ? <WsmYesNo value={fee.required} /> : <Skeleton />}
                    </TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-product-fees-empty">
                    <TableCell colSpan={FEE_COLUMNS}>
                      <Box paddingY={6}>{intl.formatMessage(feeMessages.noFees)}</Box>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>

      <CardSpacer />

      <DashboardCard data-test-id="wsm-product-compliance">
        <DashboardCard.Header>
          <DashboardCard.Title>
            {intl.formatMessage(productTabMessages.complianceTitle)}
          </DashboardCard.Title>
          <DashboardCard.Toolbar>
            {/* There is no create mutation: the compliance page upserts, and it
                is keyed by the product, so add and edit are the same URL. */}
            <Button
              variant="secondary"
              onClick={() => navigate(wsmComplianceUrl(productId))}
              data-test-id="wsm-product-compliance-cta"
            >
              {intl.formatMessage(
                compliance ? productTabMessages.editCompliance : productTabMessages.addCompliance,
              )}
            </Button>
          </DashboardCard.Toolbar>
        </DashboardCard.Header>
        <DashboardCard.Content>
          {loading && !compliance ? (
            <Skeleton />
          ) : compliance ? (
            <Box display="grid" gap={4}>
              <WsmRailRow label={intl.formatMessage(complianceMessages.colProp65)}>
                <WsmYesNo value={compliance.prop65} tone="warning" />
              </WsmRailRow>
              <WsmRailRow label={intl.formatMessage(complianceMessages.colRestrictedStates)}>
                {formatStateCodes(compliance.restrictedStates) ||
                  intl.formatMessage(complianceMessages.none)}
              </WsmRailRow>
              <WsmRailRow label={intl.formatMessage(complianceMessages.colZones)}>
                {compliance.includeShippingZones.map(zone => zone.name).join(", ") ||
                  intl.formatMessage(complianceMessages.noZoneLimit)}
              </WsmRailRow>
            </Box>
          ) : (
            <Text size={3} color="default2" data-test-id="wsm-product-compliance-empty">
              {intl.formatMessage(productTabMessages.noCompliance)}
            </Text>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </Box>
  );
};

export default WsmProductComposeCard;
